/**
 * Post-load material fixups for Character-Creator / Reallusion GLB exports
 * (Riya, Anne Hathaway — anything on the `cc-base` or `cc-base-noprefix`
 * rig profile).
 *
 * CC exports stack several skinned meshes on the same skeleton:
 *
 *   • `CC_Base_Body`  — head/arm/torso/leg skin (multiple materials, 1 mesh)
 *   • Clothing shells — shirt, skirt, jeans, camisole, jacket, heels, etc.
 *     (mesh names vary per outfit; an exhaustive list isn't tractable)
 *   • Hair / scalp / lash / cornea / tearline — semi-transparent cards
 *
 * Two distinct rendering problems result:
 *
 *   1. Z-fighting between body skin and clothing layered directly on top
 *      of it. As the camera orbits or the character moves, the GPU can't
 *      decide which surface is in front and the materials flicker — the
 *      "skirt glitching over the shirt" symptom. Worse: on clothing with
 *      `alphaMap`, body skin would punch straight through cloth seams.
 *
 *   2. Hair/cornea/tearline cards lose their soft-edge alpha and read
 *      as solid white halos, especially against dark backgrounds.
 *
 * `fixCcGlbLayeringMaterials` solves (1) with `polygonOffset` plus
 * `renderOrder` — clothing pushed slightly toward the camera, the
 * `cc_base_body` skin slots pushed slightly back. Clothing detection
 * uses **exclusion**: anything that isn't body skin or hair/eye is
 * treated as clothing. This is deliberate — explicit allowlists missed
 * outfit-specific mesh names (e.g. `Chunky_Highlights_Bangs`,
 * `Female_Angled`, `Twist_Half_Up`) and broke whenever a new export
 * arrived with different naming.
 *
 * `fixCcTransparentMaterials` solves (2) — and an important corollary:
 * clothing with an `alphaMap` keeps `depthWrite=true`, only true
 * hair/eye geometry gets `depthWrite=false`. Without this, body skin
 * could leak through clothing where alpha maps existed.
 *
 * Both functions are pure post-load patches: they take the loaded root,
 * walk it, mutate materials in place, and return. No coupling to mixer,
 * animation state, or `figure.ts` internals.
 */

import {
  DoubleSide,
  FrontSide,
  MeshStandardMaterial,
  type Material,
  type Mesh,
  type Object3D,
} from 'three';

/**
 * Names of nodes that ship with CC GLBs exported from Blender + Faceit
 * (the rig-and-blendshape authoring add-on). They are author-time helpers,
 * not part of the character: backdrop planes, empty parents for the rig UI,
 * `facial_landmarks` (a mesh of small white spheres at landmark positions
 * on the face — visually identical to "white eyes" if it isn't stripped),
 * and the FaceitRig + cs_fist control-shape meshes.
 *
 * Matched by exact name (case-insensitive) on whole-word match.
 */
const BLENDER_STUDIO_NODE_NAMES = /^(Backdrop|Empty|facial_landmarks|FaceitRig|cs_fist)$/i;
/** Default-named extra Plane meshes (`Plane`, `Plane.001`, …). */
const BLENDER_STUDIO_PLANE_NAMES = /^Plane(\.\d+)?$/i;

/**
 * Remove Blender / Faceit author-time helper nodes that sometimes ship
 * inside CC GLB exports. Returns the count of removed nodes for logging.
 *
 * Run once on GLB load before any material fix passes — these helpers
 * are usually hidden in the source `.blend` file but exporters include
 * them anyway, and they render as floating planes / clusters of white
 * spheres over the character.
 */
export function stripBlenderStudioExtras(root: Object3D): number {
  const remove: Object3D[] = [];
  root.traverse((obj) => {
    if (BLENDER_STUDIO_NODE_NAMES.test(obj.name)) {
      remove.push(obj);
      return;
    }
    if ((obj as Mesh).isMesh && BLENDER_STUDIO_PLANE_NAMES.test(obj.name)) {
      remove.push(obj);
    }
  });
  for (const obj of remove) {
    obj.parent?.remove(obj);
  }
  return remove.length;
}

/** CC body-skin mesh prefix — same on every CC export. */
const BODY_SKIN_NAME_PATTERN = /^cc_base_body/;


/**
 * Mesh-name fragments identifying hair / eye / cornea / tearline geometry
 * inside a CC GLB. Matched against the lower-cased mesh name.
 *
 * Anything matching this is exempt from clothing depth-offset (it sits
 * in front of the body anyway, doesn't z-fight with skin).
 */
const HAIR_OR_EYE_NAME_PATTERN = /hair|scalp|lash|eyelash|eye|cornea|tearline/;

/**
 * Material names within `CC_Base_Body` that represent skin actually
 * covered by clothing. The head/face skin material isn't pushed back —
 * nothing covers it.
 */
const COVERED_SKIN_MATERIAL_PATTERN = /skin_arm|skin_body|skin_leg/;

/**
 * Combined `mesh.name | material.name` fragments that mark a material
 * as "hair-like" — needs cutout alpha and double-sided rendering, and
 * (importantly) `depthWrite=false` so soft edges resolve in the
 * transparent pass.
 *
 * Includes:
 *   • `angled`       — CC `Female_Angled` face overlays
 *   • `transparency` — CC bakes that suffix into hair material names
 *                      (e.g. `Hair_T_Transparency`, `Scalp_Transparency`)
 *   • `occlusion`    — `Std_Eye_Occlusion_R/L`. CC ships this material as
 *                      BLEND with the alpha gradient baked into the
 *                      `baseColorTexture` alpha channel (NOT a separate
 *                      `alphaMap`). Without this entry, the spurious-BLEND
 *                      detection downstream force-flips it to opaque and
 *                      the gradient renders as solid white over the eye
 *                      ("completely white eyes" symptom).
 */
const HAIR_LIKE_PATTERN =
  /hair|scalp|lash|eyelash|tearline|angled|transparency|cornea|occlusion/;

/**
 * Mesh-name fragments identifying **thin / draped cloth** that has no
 * volume — capes, cloaks, sashes, tassels, ribbons, hanging dangles.
 * These meshes are usually authored as a single sheet of geometry with
 * no inside; the GLTF default of `FrontSide` rendering then makes them
 * visible from one direction only. The Warrior export's `Cloak` and
 * `Dangles` meshes are the canonical case — visible from behind, gone
 * when viewed from the front.
 *
 * Does NOT include `dress` / `skirt` / `shirt` / `coat` — those are
 * closed shells where DoubleSide would z-fight with itself.
 */
const THIN_CLOTH_NAME_PATTERN = /cloak|cape|sash|tassel|ribbon|dangle/;

/**
 * Push CC clothing toward the camera and covered skin away, using
 * `polygonOffset`. Run once on GLB load; values are tuned conservatively
 * — large offsets cause silhouette artifacts at glancing angles.
 *
 * Clothing offset:  factor `+2`, units `+8`, `renderOrder = 2`,
 *                   `FrontSide` + `depthWrite` for opaque materials.
 * Skin offset:      factor `-1`, units `-4`, only on the three covered
 *                   skin materials (arms / body / legs).
 *
 * Clothing detection uses **exclusion**, not an allowlist: any mesh
 * that isn't body-skin or hair/eye is treated as clothing. CC outfit
 * meshes have wildly varying names (`Pencil_skirt`, `Camisole`,
 * `Close_collar_short_sleeves_shirt`, `Jewelry_Barrette_*`) and
 * matching them by name has historically broken on every new export.
 */
export function fixCcGlbLayeringMaterials(root: Object3D): void {
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return;
    const mesh = obj as Mesh;
    const name = mesh.name.toLowerCase();
    const isBodySkin = BODY_SKIN_NAME_PATTERN.test(name);
    const isHairOrEye = HAIR_OR_EYE_NAME_PATTERN.test(name);
    const isClothing = !isBodySkin && !isHairOrEye;
    // Thin draped cloth (Cloak, Dangles, sash, ribbons) needs DoubleSide
    // because it's authored as a single sheet of geometry. Without it
    // the cape disappears when viewed from the side it doesn't face.
    const isThinCloth = isClothing && THIN_CLOTH_NAME_PATTERN.test(name);

    const pushForward = (mat: Material) => {
      // `instanceof` catches both MeshStandardMaterial and MeshPhysicalMaterial
      // (which GLTFLoader creates whenever a GLB uses `KHR_materials_specular`
      // or `KHR_materials_ior`, e.g. Anne's export).
      if (!(mat instanceof MeshStandardMaterial)) return;
      const std = mat;
      std.polygonOffset = true;
      std.polygonOffsetFactor = 2;
      std.polygonOffsetUnits = 8;
      if (!std.alphaMap) {
        // Thin cloth: render both faces (cape visible from any angle).
        // Solid clothing shells stay FrontSide to avoid self z-fighting.
        std.side = isThinCloth ? DoubleSide : FrontSide;
        std.depthWrite = true;
      } else if (isThinCloth) {
        // Thin cloth WITH an alphaMap (frayed edges, lace cape) — both
        // sides still need to render; the transparency pass below will
        // tune depthWrite/alphaTest for it.
        std.side = DoubleSide;
      }
      std.needsUpdate = true;
    };

    const pushBack = (mat: Material) => {
      if (!(mat instanceof MeshStandardMaterial)) return;
      const label = (mat.name || '').toLowerCase();
      if (!COVERED_SKIN_MATERIAL_PATTERN.test(label)) return;
      const std = mat;
      std.polygonOffset = true;
      std.polygonOffsetFactor = -1;
      std.polygonOffsetUnits = -4;
      std.needsUpdate = true;
    };

    if (isClothing) {
      mesh.renderOrder = 2;
      if (Array.isArray(mesh.material)) mesh.material.forEach(pushForward);
      else if (mesh.material) pushForward(mesh.material);
    } else if (isBodySkin) {
      if (Array.isArray(mesh.material)) mesh.material.forEach(pushBack);
      else if (mesh.material) pushBack(mesh.material);
    }
  });
}

/**
 * Sort out CC GLB transparency. Three independent cases, each with its
 * own correct rendering setup:
 *
 *   1. **Hair-like** (hair / scalp / lash / cornea / tearline / angled /
 *      `*_Transparency`) — real soft-edge alpha cards. Render in the
 *      transparent pass with `depthWrite=false` and double-sided faces
 *      so the soft edges blend correctly.
 *
 *   2. **Real alpha** (any non-hair material that owns an `alphaMap`) —
 *      clothing with genuine cut-outs (lace, mesh, edge falloff). Stay
 *      transparent **but with `depthWrite=true` and `alphaTest=0.35`**,
 *      so the body skin can't punch through cloth seams.
 *
 *   3. **Spurious BLEND** — non-hair material that GLTFLoader marked
 *      transparent (because the GLB declares `alphaMode: BLEND`) but
 *      that has no separate `alphaMap`. CC / Blender exports drop into
 *      this branch for two distinct reasons:
 *
 *        a. Genuinely no per-pixel alpha (Anne's body-skin materials —
 *           the Principled-BSDF Alpha socket gets flagged BLEND on
 *           export even when alpha is 1.0 everywhere).
 *        b. **Alpha packed into the `baseColorTexture` itself** (Reallusion
 *           CC bakes Diffuse + Opacity into a single RGBA — names like
 *           `Jeans_Diffuse-Jeans_Opacity`). `material.alphaMap` is null,
 *           but `material.map` carries a meaningful alpha channel.
 *
 *      Force `transparent=false` + `depthWrite=true` to leave the
 *      transparent pass (kills the depth-sort flicker), AND set
 *      `alphaTest=0.5` so case (b) honours its packed-alpha cutouts
 *      (sleeve openings, jeans rips, shoe heel cups). For case (a) the
 *      alpha is 1.0 everywhere, so the discard test is a no-op
 *      (~negligible perf cost, no visual change).
 *
 * Fully-opaque materials with no transparency hints fall through every
 * branch untouched.
 */
export function fixCcTransparentMaterials(root: Object3D): void {
  let hairCount = 0;
  let alphaCount = 0;
  let forcedOpaqueCount = 0;
  let untouchedCount = 0;
  const forcedOpaqueNames: string[] = [];

  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return;
    const mesh = obj as Mesh;
    const label = `${mesh.name}|${(mesh.material as Material)?.name ?? ''}`.toLowerCase();
    const isHairLike = HAIR_LIKE_PATTERN.test(label);
    const apply = (mat: Material) => {
      // `instanceof` covers both MeshStandardMaterial and MeshPhysicalMaterial.
      // See note in `fixCcGlbLayeringMaterials`.
      if (!(mat instanceof MeshStandardMaterial)) {
        untouchedCount++;
        return;
      }
      const std = mat;

      if (isHairLike) {
        std.transparent = true;
        std.depthWrite = false;
        std.side = DoubleSide;
        if (std.alphaMap) std.alphaTest = 0.35;
        std.needsUpdate = true;
        hairCount++;
        return;
      }

      if (std.alphaMap) {
        std.transparent = true;
        std.depthWrite = true;
        std.alphaTest = 0.35;
        std.needsUpdate = true;
        alphaCount++;
        return;
      }

      // Spurious BLEND from Blender exporter, OR clothing whose opacity
      // is packed into the `baseColorTexture` alpha channel rather than
      // a separate `alphaMap` (CC's standard Diffuse-Opacity packing).
      // Either way: leave the transparent pass to kill depth-sort
      // flicker, but keep an alphaTest discard so any meaningful packed
      // alpha (sleeve openings, jeans rips, shoe heel cups) still cuts
      // out instead of rendering its skin-toned cutout RGB through the
      // garment as a "patch".
      //
      // For clothing we ALSO override the FrontSide that
      // `fixCcGlbLayeringMaterials` set, switching to DoubleSide. CC
      // garment exports sporadically have flipped face normals on a
      // handful of triangles (a known Reallusion/Blender quirk). With
      // FrontSide those triangles get back-face-culled when their
      // flipped normals face the camera, producing camera-dependent
      // holes that move as the user orbits. DoubleSide renders both
      // faces; self-z-fighting isn't a concern in the opaque pass
      // because depthWrite=true + alphaTest=0.5 lets the depth test
      // resolve which side wins per-pixel. Body skin stays FrontSide —
      // it's a closed solid with correct normals, no point doubling
      // fragment work.
      if (std.transparent) {
        std.transparent = false;
        std.depthWrite = true;
        std.alphaTest = 0.5;
        const isBodySkin = BODY_SKIN_NAME_PATTERN.test(mesh.name.toLowerCase());
        if (!isBodySkin) std.side = DoubleSide;
        std.needsUpdate = true;
        forcedOpaqueCount++;
        forcedOpaqueNames.push(mat.name || mesh.name);
        return;
      }

      untouchedCount++;
    };
    if (Array.isArray(mesh.material)) mesh.material.forEach(apply);
    else if (mesh.material) apply(mesh.material);
  });

  console.log(
    `[cc-glb-materials] transparency fix: ${hairCount} hair, ${alphaCount} alphaMap, ` +
      `${forcedOpaqueCount} BLEND→opaque (${forcedOpaqueNames.join(', ')}), ` +
      `${untouchedCount} untouched`,
  );
}

/**
 * Per-slot regexes matching a CC body-skin material name.
 *
 * CC's `CC_Base_Body` mesh ships with a fixed set of skin materials
 * named `Std_Skin_<slot>`. Each slot covers a region of the body and is
 * hidden under a different garment:
 *
 *   - `body` — torso / chest / back (under shirts and tops)
 *   - `arm`  — entire arm + hand     (under long sleeves)
 *   - `leg`  — entire leg + foot     (under pants / jeans + shoes)
 *   - `head` — face / neck / ears   (never hidden — always exposed)
 *
 * Hide-slot decisions are character-specific because the same slot can
 * be either fully covered (Anne's jeans → leg) or partially exposed
 * (Riya's pencil skirt to the knee → leg stays visible).
 */
const SKIN_SLOT_PATTERNS: Record<string, RegExp> = {
  body: /^std_skin_body/i,
  arm: /^std_skin_arm/i,
  leg: /^std_skin_leg/i,
  head: /^std_skin_head/i,
};
export type CcSkinSlot = keyof typeof SKIN_SLOT_PATTERNS;

/**
 * Hide one or more body-skin slots so pose-driven clothing skinning
 * gaps (after weight-transfer remediation in `scripts/cc-weight-transfer.mjs`)
 * can't expose skin through the residual seam.
 *
 * Mechanism: walk the body mesh, set `material.visible = false` on
 * primitives whose material name matches a requested slot pattern.
 * With the clothing rendered DoubleSide (per `fixCcTransparentMaterials`)
 * and the skin slot absent, any residual tear lets the camera see the
 * inside of the *opposite* side of the closed clothing shell. three.js
 * auto-flips normals for back-facing fragments on
 * MeshStandardMaterial, so the visible colour is the clothing's own
 * colour — not background, not skin.
 *
 * **Coverage caveat per slot:**
 *   - `body` — safe only with a high-collar, long-hem top. Low-cut
 *     camisoles, crop tops, deep V-necks would expose the inside of
 *     the shirt through bare chest / back.
 *   - `leg`  — safe only with full-length pants + closed shoes. Skirts,
 *     shorts, sandals, bare feet would expose the inside of the
 *     trouser leg through the bare leg.
 *   - `arm`  — safe only with long sleeves + gloves. Short sleeves
 *     leave the forearm exposed.
 *
 * Gate per-character via `hideSkinSlots` in the manifest.
 */
export function hideBodySkinSlots(root: Object3D, slots: CcSkinSlot[]): void {
  if (slots.length === 0) return;
  const patterns = slots.map((s) => ({ slot: s, re: SKIN_SLOT_PATTERNS[s] }));
  let hidden = 0;
  const hiddenLog: string[] = [];
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return;
    const mesh = obj as Mesh;
    if (!BODY_SKIN_NAME_PATTERN.test(mesh.name.toLowerCase())) return;
    const apply = (mat: Material) => {
      const matName = (mat.name || '').toLowerCase();
      for (const { slot, re } of patterns) {
        if (re.test(matName)) {
          mat.visible = false;
          hidden++;
          hiddenLog.push(`${slot}:${mat.name}`);
          return;
        }
      }
    };
    if (Array.isArray(mesh.material)) mesh.material.forEach(apply);
    else if (mesh.material) apply(mesh.material);
  });
  console.log(
    `[cc-glb-materials] body-skin slots hidden (${hidden}): ${hiddenLog.join(', ') || '(none matched)'}`,
  );
}

